import { MAX_HUB_FOLDER_DEPTH } from 'app-constants'
import { compareNames } from 'helpers/compare'
import { IHubFolder, ISidebarMoveItem, ISidebarResource, SidebarResourceKind } from 'types'

export type DropZone = 'before' | 'after' | 'into'

/**
 * How this viewer has the sidebar sorted: the hub's shared order (`manual`), A–Z by name, or newest
 * first. Only the viewer's display: it never changes the shared order.
 */
export const SIDEBAR_SORTS = ['manual', 'name', 'created'] as const
export type SidebarSort = (typeof SIDEBAR_SORTS)[number]

/** What the Sort menu, and anything saying how the sidebar is sorted, calls each sort. */
export const SORT_LABEL: Record<SidebarSort, string> = { manual: 'Manual', name: 'Name', created: 'Date created' }

export interface SidebarFolderNode {
  kind: 'folder'
  id: string
  name: string
  parentId: string | null
  depth: number
  children: SidebarNode[]
}

export interface SidebarResourceNode {
  kind: SidebarResourceKind
  id: string
  name: string
  parentId: string | null
  depth: number
  resource: ISidebarResource
}

export type SidebarNode = SidebarFolderNode | SidebarResourceNode

/** Where a drop lands: the folder it goes into (null = top level) and its index there (null = append). */
export interface DropTarget {
  parentFolderId: string | null
  index: number | null
}

interface Sortable {
  publicId: string
  name: string
  position: number
  createdAt?: string
}

const byName = (a: Sortable, b: Sortable): number => compareNames(a.name, b.name)

/** Plain code-unit order, not localeCompare, so exact duplicates break ties the same way everywhere. */
const byId = (a: Sortable, b: Sortable): number => {
  if (a.publicId < b.publicId) return -1
  if (a.publicId > b.publicId) return 1
  return 0
}

/** Sidebar order: position, then name, then id — the server's (position, lower(name), id). */
const byPosition = (a: Sortable, b: Sortable): number => a.position - b.position || byName(a, b) || byId(a, b)

/** When it was created, as a number; 0 (oldest) when the server didn't say or it can't be read. */
const createdTime = (item: Sortable): number => {
  const time = item.createdAt ? Date.parse(item.createdAt) : NaN
  return Number.isNaN(time) ? 0 : time
}

const COMPARE: Record<SidebarSort, (a: Sortable, b: Sortable) => number> = {
  manual: byPosition,
  // Ties (the same name) keep the shared order.
  name: (a, b) => byName(a, b) || byPosition(a, b),
  // Newest first; ties by name.
  created: (a, b) => createdTime(b) - createdTime(a) || byName(a, b) || byId(a, b)
}

// Row ids are bare public ids (UUIDs, unique across folders, documents and tables). applyMove alone
// matches resources on kind and id, the way a move request names them.
const itemKey = (kind: ISidebarMoveItem['kind'], id: string): string => `${kind}:${id}`
/** A resource's key, its kind and id, as applyMove matches it (and useHubFolders undoes a move by). */
export const resourceKey = (r: ISidebarResource): string => itemKey(r.kind, r.publicId)

/** A parent id the tree can place things under; anything pointing at an unknown folder is top level. */
const knownParent = (known: Set<string>, parentId: string | null): string | null =>
  parentId !== null && known.has(parentId) ? parentId : null

const groupByParent = <T>(items: T[], parentOf: (item: T) => string | null): Map<string | null, T[]> => {
  const groups = new Map<string | null, T[]>()
  for (const item of items) {
    const key = parentOf(item)
    const group = groups.get(key)
    if (group) group.push(item)
    else groups.set(key, [item])
  }
  return groups
}

/**
 * The sidebar tree: each level's folders first, then its documents and tables, each group in `sort`
 * order (the hub's shared manual order by default).
 */
export const buildSidebarTree = (
  folders: IHubFolder[],
  resources: ISidebarResource[],
  sort: SidebarSort = 'manual'
): SidebarNode[] => {
  const compare = COMPARE[sort]
  const known = new Set(folders.map((f) => f.publicId))
  const childFolders = groupByParent(folders, (f) => knownParent(known, f.parentId))
  const childResources = groupByParent(resources, (r) => knownParent(known, r.folderId))
  // Folders already placed. A corrupt parent cycle (a → b → a) would otherwise recurse forever.
  const placed = new Set<string>()

  const folderNode = (f: IHubFolder, parentId: string | null, depth: number): SidebarNode => {
    placed.add(f.publicId)
    return { kind: 'folder', id: f.publicId, name: f.name, parentId, depth, children: build(f.publicId, depth + 1) }
  }
  const buildFolders = (parentId: string | null, depth: number): SidebarNode[] =>
    [...(childFolders.get(parentId) ?? [])]
      .sort(compare)
      .filter((f) => !placed.has(f.publicId))
      .map((f) => folderNode(f, parentId, depth))
  const buildResources = (parentId: string | null, depth: number): SidebarNode[] =>
    [...(childResources.get(parentId) ?? [])]
      .sort(compare)
      .map((r) => ({ kind: r.kind, id: r.publicId, name: r.name, parentId, depth, resource: r }))
  function build(parentId: string | null, depth: number): SidebarNode[] {
    return [...buildFolders(parentId, depth), ...buildResources(parentId, depth)]
  }

  const topFolders = buildFolders(null, 0)
  // Folders a parent cycle cuts off from the root get the orphans' fallback: the top level. Placing one
  // brings the rest of its cycle in beneath it.
  const stranded = folders
    .filter((f) => !placed.has(f.publicId))
    .sort(compare)
    .flatMap((f) => (placed.has(f.publicId) ? [] : [folderNode(f, null, 0)]))
  return [...topFolders, ...stranded, ...buildResources(null, 0)]
}

/** The rows on screen, top to bottom: collapsed folders hide their children. */
export const visibleRowIds = (tree: SidebarNode[], expanded: Set<string>): string[] =>
  tree.flatMap((node) =>
    node.kind === 'folder' && expanded.has(node.id) ? [node.id, ...visibleRowIds(node.children, expanded)] : [node.id]
  )

/** The row with this id, anywhere in the tree. */
export const findNode = (tree: SidebarNode[], id: string): SidebarNode | undefined => {
  for (const node of tree) {
    if (node.id === id) return node
    if (node.kind === 'folder') {
      const found = findNode(node.children, id)
      if (found) return found
    }
  }
  return undefined
}

/** Every row's folder (null at the top level), keyed by row id. */
export const parentIds = (tree: SidebarNode[]): Map<string, string | null> => {
  const parents = new Map<string, string | null>()
  const walk = (nodes: SidebarNode[]): void =>
    nodes.forEach((node) => {
      parents.set(node.id, node.parentId)
      if (node.kind === 'folder') walk(node.children)
    })
  walk(tree)
  return parents
}

/** The node's id and every id nested under it, open or not. */
export const subtreeIds = (node: SidebarNode): string[] =>
  node.kind === 'folder' ? [node.id, ...node.children.flatMap(subtreeIds)] : [node.id]

/**
 * Whether a new folder fits inside `folderId` (null = top level) without nesting past
 * MAX_HUB_FOLDER_DEPTH. A subfolder sits one level below its folder (depth 0 is a top-level folder,
 * level 1). False for a folder the tree doesn't show.
 */
export const subfolderFits = (tree: SidebarNode[], folderId: string | null): boolean => {
  if (folderId === null) return true
  const folder = findNode(tree, folderId)
  return folder !== undefined && folder.depth + 2 <= MAX_HUB_FOLDER_DEPTH
}

/** A folder's children as the tree shows them; the top level for null. */
export const childrenOf = (tree: SidebarNode[], parentId: string | null): SidebarNode[] => {
  if (parentId === null) return tree
  const parent = findNode(tree, parentId)
  return parent && parent.kind === 'folder' ? parent.children : []
}

type FolderIndex = Map<string, IHubFolder>

const indexFolders = (folders: IHubFolder[]): FolderIndex => new Map(folders.map((f) => [f.publicId, f]))

/** Walks up from `folderId`, nearest first, stopping if a corrupt cycle comes back round. */
const ancestorsFrom = (byId: FolderIndex, folderId: string | null): IHubFolder[] => {
  const chain: IHubFolder[] = []
  const visited = new Set<string>()
  let current = folderId ? byId.get(folderId) : undefined
  while (current && !visited.has(current.publicId)) {
    visited.add(current.publicId)
    chain.push(current)
    current = current.parentId ? byId.get(current.parentId) : undefined
  }
  return chain
}

/** The folder itself and every folder above it, nearest first. Empty for the top level (null). */
export const folderAncestorIds = (folders: IHubFolder[], folderId: string | null): string[] =>
  ancestorsFrom(indexFolders(folders), folderId).map((f) => f.publicId)

/** The folder and everything nested under it. */
export const descendantFolderIds = (folders: IHubFolder[], folderId: string): Set<string> => {
  const result = new Set<string>([folderId])
  let grew = true
  while (grew) {
    grew = false
    for (const f of folders) {
      if (f.parentId && result.has(f.parentId) && !result.has(f.publicId)) {
        result.add(f.publicId)
        grew = true
      }
    }
  }
  return result
}

/**
 * Levels in the subtree rooted at `folderId`, counting itself (a folder with no subfolders is 1).
 * `visited` is shared across the walk, so each folder is looked at once and a corrupt cycle ends it.
 */
const subtreeHeight = (
  childFolders: Map<string | null, IHubFolder[]>,
  folderId: string,
  visited: Set<string> = new Set()
): number => {
  visited.add(folderId)
  let tallest = 0
  for (const child of childFolders.get(folderId) ?? []) {
    if (!visited.has(child.publicId)) {
      tallest = Math.max(tallest, subtreeHeight(childFolders, child.publicId, visited))
    }
  }
  return 1 + tallest
}

/**
 * Path order, a folder at a time, so a folder is followed by its own subfolders ("Design",
 * "Design / Spec") before a sibling whose name only starts the same ("Design - old").
 */
export const comparePaths = (a: string[], b: string[]): number => {
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    const order = compareNames(a[i], b[i])
    if (order !== 0) return order
  }
  return a.length - b.length
}

/** How many documents and tables these are, in words: "3 documents", "1 document and 2 tables". */
export const countResources = (resources: Array<Pick<ISidebarResource, 'kind'>>): string => {
  const count = (n: number, noun: string): string | null => (n === 0 ? null : `${n} ${noun}${n === 1 ? '' : 's'}`)
  const documents = resources.filter((r) => r.kind === 'document').length
  return [count(documents, 'document'), count(resources.length - documents, 'table')]
    .filter((part) => part !== null)
    .join(' and ')
}

/** What pickers call the hub's root, outside every folder. */
export const TOP_LEVEL_LABEL = 'Top level'

/** How pickers show a folder. */
export interface FolderLabel {
  /** Its path from the top down, a repeated name numbered: ["Design (2)", "Drawings"]. */
  segments: string[]
  /** The path as one string, "Design (2) / Drawings": different for every folder. */
  label: string
  /** Where it comes in the sidebar (manual order, depth first). */
  rank: number
}

/**
 * Every folder's picker label, keyed by folder id, in sidebar order. A path another folder already
 * has, such as a second top-level "Design", is numbered "Design (2)", "Design (3)" in sidebar order,
 * and its subfolders follow: "Design (2) / Drawings". "Top level" is the hub root's, so a folder
 * called that is "Top level (2)".
 */
export const folderLabels = (folders: IHubFolder[]): Map<string, FolderLabel> => {
  const labels = new Map<string, FolderLabel>()
  const taken = new Set([TOP_LEVEL_LABEL])
  const walk = (nodes: SidebarNode[], parent: string[]): void => {
    for (const node of nodes) {
      if (node.kind !== 'folder') continue
      const path = [...parent, node.name].join(' / ')
      let n = 1
      while (taken.has(n === 1 ? path : `${path} (${n})`)) n++
      const name = n === 1 ? node.name : `${node.name} (${n})`
      const segments = [...parent, name]
      const label = segments.join(' / ')
      taken.add(label)
      labels.set(node.id, { segments, label, rank: labels.size })
      walk(node.children, segments)
    }
  }
  walk(buildSidebarTree(folders, []), [])
  return labels
}

/** Picker order: by path, so a folder's subfolders follow it, and the sidebar's order between look-alikes. */
export const compareFolderLabels = (a: FolderLabel, b: FolderLabel): number =>
  comparePaths(a.segments, b.segments) || a.rank - b.rank

const TOP_LEVEL: FolderLabel = { segments: [], label: TOP_LEVEL_LABEL, rank: -1 }

/**
 * Picker options for documents and tables (each option's value is the resource's public id), grouped
 * by the folder the resource sits in: "Top level" first, then each folder under its label in path
 * order, and A–Z by label within a group. A resource the sidebar doesn't list, such as a table created
 * a moment ago that the next refresh will bring in, or one in a folder that isn't listed, counts as
 * top level, as it does in the sidebar.
 */
export const groupOptionsByFolder = <T extends { value: string; label: string }>(
  options: T[],
  folders: IHubFolder[],
  resources: ISidebarResource[]
): Array<T & { group: string }> => {
  const folderOf = new Map(resources.map((r) => [r.publicId, r.folderId]))
  const labels = folderLabels(folders)
  const folderLabelOf = (resourceId: string): FolderLabel => {
    const folderId = folderOf.get(resourceId)
    return (folderId && labels.get(folderId)) || TOP_LEVEL
  }
  return options
    .map((option) => ({ option, folder: folderLabelOf(option.value) }))
    .sort((a, b) => compareFolderLabels(a.folder, b.folder) || compareNames(a.option.label, b.option.label))
    .map(({ option, folder }) => ({ ...option, group: folder.label }))
}

/**
 * Where dropping `dragged` on row `overId` would put it, or null when the drop isn't allowed (so no
 * indicator shows): onto a dragged row, a folder into itself or a subfolder, or nesting deeper than
 * MAX_HUB_FOLDER_DEPTH. `into` only means into on a folder row; on a document or table row it is treated
 * as `before`.
 *
 * The index counts over the target's children as the tree shows them (folders first) with the dragged
 * items removed — the same thing the server counts. A non-owner Builder may not see every resource,
 * so for them the index is over visible items only and can land slightly off around hidden ones.
 */
export const resolveDrop = (
  tree: SidebarNode[],
  folders: IHubFolder[],
  overId: string,
  zone: DropZone,
  dragged: ISidebarMoveItem[]
): DropTarget | null => {
  const over = findNode(tree, overId)
  if (!over) return null
  const draggedIds = new Set(dragged.map((d) => d.id))
  if (draggedIds.has(over.id)) return null

  const intoFolder = zone === 'into' && over.kind === 'folder'
  let target: DropTarget
  if (intoFolder) {
    target = { parentFolderId: over.id, index: null }
  } else {
    const siblings = childrenOf(tree, over.parentId).filter((n) => !draggedIds.has(n.id))
    const at = siblings.indexOf(over)
    target = { parentFolderId: over.parentId, index: zone === 'after' ? at + 1 : at }
  }

  const draggedFolderIds = dragged.filter((d) => d.kind === 'folder').map((d) => d.id)
  return canNestFoldersIn(folders, draggedFolderIds, target.parentFolderId) ? target : null
}

/**
 * Whether the folders `folderIds` may move into `parentFolderId` (null = top level): not into
 * themselves or a subfolder, and not so deep that anything would nest past MAX_HUB_FOLDER_DEPTH.
 */
export const canNestFoldersIn = (
  folders: IHubFolder[],
  folderIds: string[],
  parentFolderId: string | null
): boolean => {
  if (folderIds.length === 0) return true
  const targetAncestors = folderAncestorIds(folders, parentFolderId)
  const childFolders = groupByParent(folders, (f) => f.parentId)
  return folderIds.every(
    (id) =>
      !targetAncestors.includes(id) && targetAncestors.length + subtreeHeight(childFolders, id) <= MAX_HUB_FOLDER_DEPTH
  )
}

/**
 * `items` without those inside a folder that is also moving, at any depth: they go along with it, so
 * the subtree moves whole rather than each being re-filed into the target. The server does the same
 * (api/app/dao/hub_folder.py move_hub_items), so a move request needn't name them.
 */
export const withoutNested = (
  folders: IHubFolder[],
  resources: ISidebarResource[],
  items: ISidebarMoveItem[]
): ISidebarMoveItem[] => {
  const movingFolders = new Set(items.filter((item) => item.kind === 'folder').map((item) => item.id))
  if (movingFolders.size === 0) return items
  const byId = indexFolders(folders)
  const folderOf = new Map(resources.map((r) => [resourceKey(r), r.folderId]))
  const parentOf = (item: ISidebarMoveItem): string | null =>
    item.kind === 'folder' ? (byId.get(item.id)?.parentId ?? null) : (folderOf.get(itemKey(item.kind, item.id)) ?? null)
  return items.filter((item) => !ancestorsFrom(byId, parentOf(item)).some((f) => movingFolders.has(f.publicId)))
}

export const insertAt = <T>(list: T[], at: number, block: T[]): T[] => [
  ...list.slice(0, at),
  ...block,
  ...list.slice(at)
]

/**
 * The client's copy of the server's move (api/app/dao/hub_folder.py move_hub_items), applied
 * optimistically before the request returns:
 * - a repeated item takes one slot (first occurrence wins); unknown items are skipped
 * - an item inside a folder that is also moving stays where it is in it, and goes along (withoutNested)
 * - `index` counts over the target's children, folders first, with the moving items removed; null appends
 * - moving folders land among the target's folders and moving resources among its resources
 * - the target and every old parent are renumbered 0..n, folders and resources separately
 */
export const applyMove = (
  folders: IHubFolder[],
  resources: ISidebarResource[],
  items: ISidebarMoveItem[],
  parentFolderId: string | null,
  index: number | null
): { folders: IHubFolder[]; resources: ISidebarResource[] } => {
  const known = new Set(folders.map((f) => f.publicId))
  const folderParent = (f: IHubFolder): string | null => knownParent(known, f.parentId)
  const resourceParent = (r: ISidebarResource): string | null => knownParent(known, r.folderId)
  const folderById = new Map(folders.map((f) => [f.publicId, f]))
  const resourceByKey = new Map(resources.map((r) => [resourceKey(r), r]))

  const movingFolderIds: string[] = []
  const movingResourceKeys: string[] = []
  for (const item of withoutNested(folders, resources, items)) {
    if (item.kind === 'folder') {
      if (folderById.has(item.id) && !movingFolderIds.includes(item.id)) movingFolderIds.push(item.id)
    } else {
      const key = itemKey(item.kind, item.id)
      if (resourceByKey.has(key) && !movingResourceKeys.includes(key)) movingResourceKeys.push(key)
    }
  }
  const movingFolders = new Set(movingFolderIds)
  const movingResources = new Set(movingResourceKeys)

  const remainingFolderIds = (parent: string | null): string[] =>
    folders
      .filter((f) => folderParent(f) === parent && !movingFolders.has(f.publicId))
      .sort(byPosition)
      .map((f) => f.publicId)
  const remainingResourceKeys = (parent: string | null): string[] =>
    resources
      .filter((r) => resourceParent(r) === parent && !movingResources.has(resourceKey(r)))
      .sort(byPosition)
      .map(resourceKey)

  const siblingFolders = remainingFolderIds(parentFolderId)
  const siblingResources = remainingResourceKeys(parentFolderId)
  const at = index === null ? siblingFolders.length + siblingResources.length : Math.max(0, index)
  const folderAt = Math.min(at, siblingFolders.length)
  const resourceAt = Math.min(Math.max(0, at - siblingFolders.length), siblingResources.length)

  const folderPositions = new Map<string, number>()
  const resourcePositions = new Map<string, number>()
  const renumber = (ids: string[], positions: Map<string, number>): void =>
    ids.forEach((id, position) => positions.set(id, position))

  renumber(insertAt(siblingFolders, folderAt, movingFolderIds), folderPositions)
  renumber(insertAt(siblingResources, resourceAt, movingResourceKeys), resourcePositions)

  const oldParents = new Set<string | null>([
    ...movingFolderIds.map((id) => folderParent(folderById.get(id) as IHubFolder)),
    ...movingResourceKeys.map((key) => resourceParent(resourceByKey.get(key) as ISidebarResource))
  ])
  for (const oldParent of oldParents) {
    if (oldParent === parentFolderId) continue
    renumber(remainingFolderIds(oldParent), folderPositions)
    renumber(remainingResourceKeys(oldParent), resourcePositions)
  }

  return {
    folders: folders.map((f) => {
      const position = folderPositions.get(f.publicId)
      if (position === undefined) return f
      return movingFolders.has(f.publicId) ? { ...f, parentId: parentFolderId, position } : { ...f, position }
    }),
    resources: resources.map((r) => {
      const position = resourcePositions.get(resourceKey(r))
      if (position === undefined) return r
      return movingResources.has(resourceKey(r)) ? { ...r, folderId: parentFolderId, position } : { ...r, position }
    })
  }
}

/**
 * A move too big for one request, split into batches of at most `size` items, each with the index
 * that lands it where the single move would have. One index places a request's folders among the
 * target's folders and its resources among its resources (applyMove), so a batch never mixes the
 * two: folders go first, then resources. The server takes out only the batch it's given before
 * reading the index, so each batch's index counts the target as it will be then, items still to
 * come included: the first lands just after what precedes the whole move, each later one just
 * after the batch before. Applied in order, the batches end where applyMove of the whole move
 * would. A move that fits is one batch, as it is.
 */
export const moveBatches = (
  folders: IHubFolder[],
  resources: ISidebarResource[],
  requested: ISidebarMoveItem[],
  parentFolderId: string | null,
  index: number | null,
  size: number
): Array<{ items: ISidebarMoveItem[]; index: number | null }> => {
  const items = withoutNested(folders, resources, requested)
  if (items.length <= size) return [{ items, index }]
  const known = new Set(folders.map((f) => f.publicId))
  const resourceKeys = new Set(resources.map(resourceKey))
  const seen = new Set<string>()
  // Each known item once, as applyMove counts them.
  const unique = items.filter((item) => {
    const key = itemKey(item.kind, item.id)
    if (seen.has(key) || !(item.kind === 'folder' ? known.has(item.id) : resourceKeys.has(key))) return false
    seen.add(key)
    return true
  })
  const movingFolders = unique.filter((item) => item.kind === 'folder')
  const movingResources = unique.filter((item) => item.kind !== 'folder')
  const chunks = (list: ISidebarMoveItem[]): ISidebarMoveItem[][] =>
    Array.from({ length: Math.ceil(list.length / size) }, (_, i) => list.slice(i * size, (i + 1) * size))
  if (index === null) {
    return [...chunks(movingFolders), ...chunks(movingResources)].map((batch) => ({ items: batch, index: null }))
  }
  type State = { folders: IHubFolder[]; resources: ISidebarResource[] }
  // The target's folders, or its resources, in order.
  const targetFolders = (state: State): string[] =>
    state.folders
      .filter((f) => knownParent(known, f.parentId) === parentFolderId)
      .sort(byPosition)
      .map((f) => itemKey('folder', f.publicId))
  const targetResources = (state: State): string[] =>
    state.resources
      .filter((r) => knownParent(known, r.folderId) === parentFolderId)
      .sort(byPosition)
      .map(resourceKey)
  const done = applyMove(folders, resources, unique, parentFolderId, index)
  const batches: Array<{ items: ISidebarMoveItem[]; index: number }> = []
  let state: State = { folders, resources }
  const send = (
    moving: ISidebarMoveItem[],
    order: (state: State) => string[],
    offset: (state: State) => number
  ): void => {
    const keys = moving.map((item) => itemKey(item.kind, item.id))
    // What the whole move lands just after, if anything: it lands as one block.
    const final = order(done)
    let after: string | null = final[final.indexOf(keys[0]) - 1] ?? null
    chunks(moving).forEach((batch) => {
      const batchKeys = new Set(batch.map((item) => itemKey(item.kind, item.id)))
      const rest = order(state).filter((key) => !batchKeys.has(key))
      const at = offset(state) + (after === null ? 0 : rest.indexOf(after) + 1)
      batches.push({ items: batch, index: at })
      state = applyMove(state.folders, state.resources, batch, parentFolderId, at)
      after = itemKey(batch[batch.length - 1].kind, batch[batch.length - 1].id)
    })
  }
  send(movingFolders, targetFolders, () => 0)
  // Resources count after the target's folders, every moving folder among them by now.
  send(movingResources, targetResources, (current) => targetFolders(current).length)
  return batches
}
